import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {
    SkillsProfileTopicDataResponseDto
} from '../../../../../services/rest/api-types/SkillsProfileTopicDataResponseDto';
import {
    Chart, RadarController, Filler, Legend, LineElement, PointElement, RadialLinearScale, Tooltip,
    ChartConfiguration, ChartData, LegendItem, Plugin
} from 'chart.js';
import {TranslateService} from '@ngx-translate/core';
import {CssColorsGetter} from '../../CssColorsGetter';

Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface CanvasPoint {
    x: number;
    y: number;
}

@Component({
    selector: 'mm-skill-profile-radar-diagram',
    templateUrl: './skill-profile-radar-diagram.component.html',
    styleUrl: './skill-profile-radar-diagram.component.scss',
    standalone: false
})
export class SkillProfileRadarDiagramComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('radarCanvas')
    public radarCanvas: ElementRef<HTMLCanvasElement> | undefined;

    @Input() topicsData: SkillsProfileTopicDataResponseDto[] = [];
    @Input() learningGoal: number | undefined;

    private radarChart: Chart | undefined;

    private MIN_RADIUS = 6;
    private MAX_RADIUS = 12;
    private showGoalLine = true;
    private staticGridValues = [20, 40, 60, 80, 100];

    constructor(private host: ElementRef<HTMLElement>,
                private translate: TranslateService) {
    }

    public ngAfterViewInit(): void {
        if (this.topicsData !== undefined && this.topicsData.length > 0) {
            this.buildChart();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if ((changes['topicsData'] !== undefined || changes['learningGoal'] !== undefined) && this.radarCanvas !== undefined) {
            this.buildChart();
        }
    }

    public ngOnDestroy(): void {
        this.radarChart?.destroy();
    }

    private hexToRgb(hex: string): { r: number, g: number, b: number } {
        const h = hex.replace('#', '');
        return {
            r: parseInt(h.substring(0, 2), 16),
            g: parseInt(h.substring(2, 4), 16),
            b: parseInt(h.substring(4, 6), 16),
        };
    }

    private lerp(a: number, b: number, t: number): number {
        return a + (b - a) * t;
    }

    private mixHex(c1: string, c2: string, t: number): string {
        const A = this.hexToRgb(c1);
        const B = this.hexToRgb(c2);
        const r = Math.round(this.lerp(A.r, B.r, t));
        const g = Math.round(this.lerp(A.g, B.g, t));
        const b = Math.round(this.lerp(A.b, B.b, t));
        return `rgb(${r}, ${g}, ${b})`;
    }

    private getLearningProgressColor(learningProgress: number, colors: { [key: string]: string }): string {
        if (this.learningGoal === undefined) {
            return colors.learningGoalReachedDark;
        }
        const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));
        if (learningProgress >= this.learningGoal) {
            const t = clamp((learningProgress - this.learningGoal) / Math.max(100 - this.learningGoal, 1), 0, 1);
            return this.mixHex(colors.learningGoalReachedLight, colors.learningGoalReachedDark, t);
        }
        const t = clamp(1 - learningProgress / Math.max(this.learningGoal, 1), 0, 1);
        return this.mixHex(colors.learningGoalNotReachedLight, colors.learningGoalNotReachedDark, t);
    }

    private darkenColor(color: string, amount: number = 30): string {
        if (color.startsWith('#')) {
            color = color.replace('#', '');
            if (color.length === 3) {
                color = color.split('').map(c => c + c).join('');
            }
            const clamp = (x: number): number => Math.max(0, Math.min(255, x));
            const r = clamp(parseInt(color.substring(0, 2), 16) - amount);
            const g = clamp(parseInt(color.substring(2, 4), 16) - amount);
            const b = clamp(parseInt(color.substring(4, 6), 16) - amount);
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }

        const match = color.match(/\d+/g);
        // eslint-disable-next-line no-null/no-null
        if (match !== null) {
            const clamp = (x: number): number => Math.max(0, Math.min(255, x));
            const r = clamp(parseInt(match[0], 10) - amount);
            const g = clamp(parseInt(match[1], 10) - amount);
            const b = clamp(parseInt(match[2], 10) - amount);
            return `rgb(${r}, ${g}, ${b})`;
        }
        return color;
    }

    private getLearningProgressBorderColor(learningProgress: number, colors: { [key: string]: string }): string {
        const learningProgressColor = this.getLearningProgressColor(learningProgress, colors);
        return this.darkenColor(learningProgressColor);
    }

    private get topicLabels(): string[] {
        return this.topicsData.map((topic) => {
            if (topic.isQuestionsWithoutTopic) {
                const questionsWithoutTopic = this.translate.instant('learningGoal.insightsPopup.skillsProfile.questionsWithoutTopic');
                return `${questionsWithoutTopic} — ${topic.learningProgress} %`
            }
            return `${topic.topicName} — ${topic.learningProgress} %`
        });
    }

    private getCssColors(): { [key: string]: string } {
        const nativeElement = this.host.nativeElement;
        return {
            grid: CssColorsGetter.get(nativeElement, '--grid'),
            text: CssColorsGetter.get(nativeElement, '--text'),
            progress: CssColorsGetter.get(nativeElement, '--progress'),
            progressBackground: CssColorsGetter.get(nativeElement, '--progress-background'),
            goal: CssColorsGetter.get(nativeElement, '--goal'),
            learningGoalReachedLight: CssColorsGetter.get(nativeElement, '--learning-goal-reached-light'),
            learningGoalReachedDark: CssColorsGetter.get(nativeElement, '--learning-goal-reached-dark'),
            learningGoalNotReachedLight: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached-light'),
            learningGoalNotReachedDark: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached-dark'),
            learningGoalReachedBackground: CssColorsGetter.get(nativeElement, '--learning-goal-reached-background'),
            learningGoalNotReachedBackground: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached-background'),
            labelBackground: CssColorsGetter.get(nativeElement, '--label-background')
        };
    }

    private calculatePointRadius(): number[] {
        const uniformRadius = (this.MIN_RADIUS + this.MAX_RADIUS) / 2;
        const topicsWithQuestions = this.topicsData.filter((t): t is SkillsProfileTopicDataResponseDto & {numberOfQuestions: number} => t.numberOfQuestions !== undefined);
        if (topicsWithQuestions.length !== this.topicsData.length) {
            return new Array(this.topicsData.length).fill(uniformRadius);
        }
        const questionCounts = topicsWithQuestions.map(t => t.numberOfQuestions);
        const minQuestions = Math.min(...questionCounts);
        const maxQuestions = Math.max(...questionCounts);
        const maxZeroLearningProgressNumberOfQuestions = Math.max(...topicsWithQuestions.filter(t => t.learningProgress === 0).map(t => t.numberOfQuestions));

        if (minQuestions === maxQuestions) {
            return new Array(questionCounts.length).fill(uniformRadius);
        }

        return topicsWithQuestions.map(topic => {
            const numberOfQuestions = topic.learningProgress === 0 ? maxZeroLearningProgressNumberOfQuestions : topic.numberOfQuestions;
            const ratio = (numberOfQuestions - minQuestions) / (maxQuestions - minQuestions);
            return this.MIN_RADIUS + ratio * (this.MAX_RADIUS - this.MIN_RADIUS);
        });
    }

    private calculatePointHoverRadius(): number[] {
        const pointRadius = this.calculatePointRadius();
        return pointRadius.map(radius => radius + 2);
    }

    private buildChart(): void {
        if (this.radarCanvas === undefined || this.radarCanvas.nativeElement === undefined) {
            return;
        }

        const learningProgressData = this.topicsData.map((t) => t.learningProgress);
        const learningGoal = new Array(learningProgressData.length).fill(this.learningGoal);
        const colors = this.getCssColors();
        const progressLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningProgressLabel');
        const learningGoalLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningGoalLabel');

        const config: ChartConfiguration<'radar'> = {
            type: 'radar',
            data: {
                labels: this.topicLabels,
                datasets: [
                    {
                        label: learningGoalLabel,
                        data: learningGoal,
                        borderColor: colors.goal,
                        backgroundColor: 'transparent',
                        borderWidth: 0,
                        pointRadius: 0,
                        fill: false,
                        tension: 0,
                    },
                    {
                        label: progressLabel,
                        data: learningProgressData,
                        borderWidth: 0,
                        backgroundColor: colors.progressBackground,
                        pointBackgroundColor: learningProgressData.map((learningProgress: number): string => this.getLearningProgressColor(learningProgress, colors)),
                        pointBorderColor: learningProgressData.map((learningProgress: number): string => this.getLearningProgressBorderColor(learningProgress, colors)),
                        pointRadius: this.calculatePointRadius(),
                        pointHoverRadius: this.calculatePointHoverRadius(),
                        pointBorderWidth: 1,
                        pointHoverBorderWidth: 1,
                        fill: true,
                        tension: 0
                    }
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: {duration: 300},
                animations: {
                    x: {duration: 0},
                    y: {duration: 0}
                },
                layout: {padding: {top: 0, bottom: 0, left: 0, right: 0}},
                scales: {
                    r: {
                        beginAtZero: true,
                        suggestedMax: 100,
                        ticks: {
                            stepSize: 20,

                            backdropColor: colors.labelBackground,
                            backdropPadding: 2,
                            color: colors.text,
                        },
                        grid: {display: false, lineWidth: 0},
                        angleLines: {color: colors.grid, lineWidth: 2},
                        pointLabels: {
                            color: (ctx): string => this.getLearningProgressColor(learningProgressData[ctx.index ?? 0], colors),
                            font: {size: 13, weight: 'normal'},
                        },
                    },
                },
                plugins: {
                    tooltip: {
                        boxPadding: 8,
                        animation: false,
                        bodySpacing: 6,
                        titleFont: {size: 12, weight: 'bold'},
                        bodyFont: {size: 12, weight: 'normal'},
                        displayColors: false,
                        callbacks: {
                            title: (items): string => {
                                if (items.length === 0) {
                                    return '';
                                }

                                const firstItem = items[0];
                                const topic = this.topicsData[firstItem.dataIndex];

                                if (topic.learningProgress === 0) {
                                    const zeroProgressTopics = this.topicsData.filter(t => t.learningProgress === 0);
                                    if (zeroProgressTopics.length > 1) {
                                        return '';
                                    }
                                }
                                return firstItem.label.split(' — ')[0];
                            },
                            label: (ctx): string[] => {
                                const topic = this.topicsData[ctx.dataIndex];
                                const numberOfQuestionsLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.numberOfQuestionsLabel');
                                const learningProgressTooltip = this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningProgressTooltip');

                                if (topic.learningProgress === 0) {
                                    const lines: string[] = [];
                                    const topicName = !topic.isQuestionsWithoutTopic ? topic.topicName ?? topic.topicId : this.translate.instant('learningGoal.insightsPopup.skillsProfile.questionsWithoutTopic');

                                    const zeroProgressIndices = this.topicsData.map((t, idx) => t.learningProgress === 0 ? idx : -1).filter(idx => idx !== -1).slice(0, 6);
                                    if (zeroProgressIndices.includes(ctx.dataIndex) === true) {
                                        const isFirstZeroProgress = zeroProgressIndices[0] === ctx.dataIndex;
                                        if (!isFirstZeroProgress) {
                                            lines.push('');
                                        }

                                        lines.push(topicName);
                                        lines.push(`${learningProgressTooltip}: ${topic.learningProgress} %`);
                                        if (topic.numberOfQuestions !== undefined) {
                                            lines.push(`${numberOfQuestionsLabel}: ${topic.numberOfQuestions}`);
                                        }
                                        return lines;
                                    }
                                    return [];
                                }
                                const learningProgressInfo = `${learningProgressTooltip}: ${topic.learningProgress} %`;
                                if (topic.numberOfQuestions === undefined) {
                                    return [learningProgressInfo];
                                }
                                const numberOfQuestionsInfo = `${numberOfQuestionsLabel}: ${topic.numberOfQuestions}`;
                                return [learningProgressInfo, numberOfQuestionsInfo];
                            },
                        }
                    },
                    legend: {
                        position: 'bottom',
                        align: 'center',
                        reverse: true,
                        labels: {
                            padding: 28,
                            color: colors.text,
                            usePointStyle: true,
                            font: {size: 12, weight: 'normal'},
                            generateLabels: (chart): LegendItem[] => {
                                const datasets = chart.data.datasets;
                                return datasets.filter(dataset => !(dataset.label === learningGoalLabel && this.learningGoal === undefined)).map((dataset, i) => ({
                                    text: dataset.label === learningGoalLabel ? `${dataset.label}  ⚑ ${this.learningGoal} %` : dataset.label ?? '',
                                    fillStyle: dataset.label === progressLabel ? colors.learningGoalReachedDark : dataset.borderColor as string,
                                    strokeStyle: dataset.label === progressLabel ? this.darkenColor(colors.learningGoalReachedDark) : dataset.borderColor as string,
                                    lineWidth: 1,
                                    pointStyle: 'circle',
                                    index: i,
                                    datasetIndex: i,
                                    fontColor: colors.text,
                                    hidden: dataset.label === progressLabel ? false : !chart.isDatasetVisible(i)
                                }));
                            }
                        },
                        onClick: (e, legendItem, legend): void => {
                            const index = legendItem.datasetIndex!;
                            const chart = legend.chart;
                            const progressIndex = chart.data.datasets.findIndex(ds => ds.label === progressLabel);
                            const learningGoalIndex = chart.data.datasets.findIndex(ds => ds.label === learningGoalLabel);
                            if (index !== progressIndex) {
                                const meta = chart.getDatasetMeta(index);
                                meta.hidden = meta.hidden !== true;
                            }
                            if (index === learningGoalIndex) {
                                this.showGoalLine = !this.showGoalLine;
                            }
                            chart.update();
                        },
                        onHover: (event, legendItem, legend): void => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined && legendItem.text !== progressLabel) {
                                canvas.style.cursor = 'pointer';
                            }

                        },
                        onLeave: (event, legendItem, legend): void => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined && legendItem.text !== progressLabel) {
                                canvas.style.cursor = 'default';
                            }
                        }
                    },
                },
            },
            plugins: [this.setLearningGoalPlugin(colors), this.setWhiteBackgroundWhenSave()],
        };

        const ctx = this.radarCanvas.nativeElement.getContext('2d')!;
        this.radarChart = new Chart(ctx, config);
    }

    private setLearningGoalPlugin(colors: { [key: string]: string }): Plugin {
        return {
            id: 'learningGoalBackgroundPlugin',
            beforeDraw: (chart): void => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const {ctx, data, scales} = chart as any;
                const rScale = scales.r;
                // eslint-disable-next-line no-null/no-null
                if (rScale === undefined || rScale === null) {
                    return
                }

                const goalValue = this.learningGoal;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const goalPts = data.labels.map((_: any, i: number) =>
                    rScale.getPointPositionForValue(i, goalValue)
                );

                this.drowRedLine(ctx, goalPts, colors);
                this.drowGreenBackground(data, rScale, ctx, goalPts, colors);
                this.drowGridLines(data, rScale, ctx, colors);
            }
        };
    }

    private drowRedLine(ctx: CanvasRenderingContext2D, goalPts: CanvasPoint[], colors: { [p: string]: string }): void {
        ctx.save();
        // red zone (below goal)
        ctx.beginPath();
        ctx.moveTo(goalPts[0].x, goalPts[0].y);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        goalPts.forEach((p: any) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fillStyle = colors.learningGoalNotReachedBackground;
        ctx.fill();
        ctx.restore();
    }

    private drowGreenBackground(data: ChartData<'radar'>, rScale: RadialLinearScale, ctx: CanvasRenderingContext2D, goalPts: CanvasPoint[], colors: { [p: string]: string }): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const outerPts = (data.labels ?? []).map((_: any, i: number) =>
            rScale.getPointPositionForValue(i, rScale.max)
        );
        // green zone (above goal)
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(outerPts[0].x, outerPts[0].y);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        outerPts.forEach((p: any) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.moveTo(goalPts[0].x, goalPts[0].y);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        goalPts.forEach((p: any) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fillStyle = colors.learningGoalReachedBackground;
        ctx.fill('evenodd');
        ctx.restore();
    }

    private drowGridLines(data: ChartData<'radar'>, rScale: RadialLinearScale, ctx: CanvasRenderingContext2D, colors: { [p: string]: string }): void {
        const gridValues = [...this.staticGridValues];
        if (this.learningGoal !== undefined && this.staticGridValues.find(v => v === this.learningGoal) === undefined) {
            gridValues.push(this.learningGoal)
        }
        for (const value of gridValues) {
            if (value === this.learningGoal && this.showGoalLine === false) {
                if (this.staticGridValues.includes(value)) {
                    this.drowGridLine(data, rScale, value, ctx, colors.grid);
                }
            } else {
                const color = value === this.learningGoal ? colors.goal : colors.grid;
                this.drowGridLine(data, rScale, value, ctx, color);
            }
        }
    }

    private drowGridLine(data: ChartData<'radar'>, rScale: RadialLinearScale, value: number, ctx: CanvasRenderingContext2D, color: string): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pts = (data.labels ?? []).map((_: any, i: number) =>
            rScale.getPointPositionForValue(i, value)
        );
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pts.forEach((p: any) => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.stroke();
        ctx.restore();
    }

    private setWhiteBackgroundWhenSave(): Plugin {
        return {
            id: 'whiteBackground',
            beforeDraw: (chart): void => {
                const ctx = chart.ctx;
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        };
    }
}
